import { createBaseApi } from '../baseApi';
import { API_ENDPOINTS } from '../../../utils/config';

// Create base person image API
const personImageApi = createBaseApi('personImageApi');

// Enhanced API with person image endpoints
export const enhancedPersonImageApi = personImageApi.injectEndpoints({
  endpoints: (builder) => ({
    // Get all user's person images
    getUserPersonImages: builder.query({
      query: ({ limit = 20, start = 0, active, sort = 'created_at:desc' } = {}) => ({
        url: API_ENDPOINTS.PERSON_IMAGE.BASE,
        method: 'GET',
        params: {
          _limit: limit,
          _start: start,
          active,
          _sort: sort
        }
      }),
      providesTags: (result) => 
        result?.personImages
          ? [
              'PersonImage',
              ...result.personImages.map(({ id }) => ({ type: 'PersonImage', id }))
            ]
          : ['PersonImage'],
      transformResponse: (response) => {
        if (response?.personImages) {
          return {
            images: response.personImages.map(item => ({
              id: item.id,
              fileName: item.fileName,
              displayName: item.displayName,
              isDefault: item.isDefault || false,
              image: item.image ? {
                id: item.image.id,
                url: item.image.url
              } : null,
              removedBgImage: item.removedBgImage ? {
                id: item.removedBgImage.id,
                url: item.removedBgImage.url
              } : null,
              thumbnail: item.thumbnail ? {
                id: item.thumbnail.id,
                url: item.thumbnail.url
              } : null,
              isActive: item.isActive,
              imageHash: item.imageHash,
              backgroundRemoved: item.backgroundRemoved || false,
              useRemovedImage: item.useRemovedImage !== undefined ? item.useRemovedImage : true,
              usageCount: item.usageCount || 0,
              lastUsed: item.lastUsed,
              createdAt: item.created_at,
              updatedAt: item.updated_at
            })),
            pagination: response.pagination
          };
        }
        return { images: [], pagination: { total: 0, hasMore: false } };
      }
    }),

    // Get latest active person image
    getLatestPersonImage: builder.query({
      query: () => ({
        url: API_ENDPOINTS.PERSON_IMAGE.LATEST,
        method: 'GET'
      }),
      providesTags: [{ type: 'PersonImage', id: 'LATEST' }],
      transformResponse: (response) => {
        // Handle new response format: { data: null, message: '...' } when no person image
        if (response && response.data === null) {
          return null;
        }
        
        // Handle direct response format (existing person image)
        if (response && response.id) {
          return {
            id: response.id,
            fileName: response.fileName,
            image: response.image ? {
              id: response.image.id,
              url: response.image.url
            } : null,
            removedBgImage: response.removedBgImage ? {
              id: response.removedBgImage.id,
              url: response.removedBgImage.url
            } : null,
            thumbnail: response.thumbnail ? {
              id: response.thumbnail.id,
              url: response.thumbnail.url
            } : null,
            isActive: response.isActive,
            imageHash: response.imageHash,
            backgroundRemoved: response.backgroundRemoved || false,
            useRemovedImage: response.useRemovedImage !== undefined ? response.useRemovedImage : true,
            usageCount: response.usageCount || 0,
            lastUsed: response.lastUsed,
            createdAt: response.created_at,
            updatedAt: response.updated_at
          };
        }
        return null;
      }
    }),

    // Upload person image with file
    uploadPersonImage: builder.mutation({
      query: ({ file, fileName, thumbnail }) => {
        const formData = new FormData();
        formData.append('image', file);
        if (fileName) {
          formData.append('fileName', fileName);
        }
        if (thumbnail) {
          formData.append('thumbnail', thumbnail);
        }

        return {
          url: API_ENDPOINTS.PERSON_IMAGE.UPLOAD,
          method: 'POST',
          body: formData
        };
      },
      invalidatesTags: ['PersonImage', { type: 'PersonImage', id: 'LATEST' }],
      transformResponse: (response) => {
        if (response?.success && response?.data) {
          const item = response.data;
          return {
            id: item.id,
            fileName: item.fileName,
            image: item.image ? {
              id: item.image.id,
              url: item.image.url
            } : null,
            removedBgImage: item.removedBgImage ? {
              id: item.removedBgImage.id,
              url: item.removedBgImage.url
            } : null,
            thumbnail: item.thumbnail ? {
              id: item.thumbnail.id,
              url: item.thumbnail.url
            } : null,
            isActive: item.isActive,
            imageHash: item.imageHash,
            backgroundRemoved: item.backgroundRemoved || false,
            useRemovedImage: item.useRemovedImage !== undefined ? item.useRemovedImage : true,
            usageCount: item.usageCount || 0,
            lastUsed: item.lastUsed,
            createdAt: item.created_at,
            updatedAt: item.updated_at
          };
        }
        throw new Error(response?.message || 'Upload failed');
      }
    }),

    // Create person image with existing file IDs
    createPersonImageWithFiles: builder.mutation({
      query: ({ imageId, thumbnailId, fileName }) => ({
        url: API_ENDPOINTS.PERSON_IMAGE.CREATE_WITH_FILES,
        method: 'POST',
        body: {
          imageId,
          thumbnailId,
          fileName
        }
      }),
      invalidatesTags: ['PersonImage', { type: 'PersonImage', id: 'LATEST' }],
      transformResponse: (response) => {
        if (response?.success && response?.data) {
          const item = response.data;
          return {
            id: item.id,
            fileName: item.fileName,
            image: item.image ? {
              id: item.image.id,
              url: item.image.url
            } : null,
            removedBgImage: item.removedBgImage ? {
              id: item.removedBgImage.id,
              url: item.removedBgImage.url
            } : null,
            thumbnail: item.thumbnail ? {
              id: item.thumbnail.id,
              url: item.thumbnail.url
            } : null,
            isActive: item.isActive,
            imageHash: item.imageHash,
            backgroundRemoved: item.backgroundRemoved || false,
            useRemovedImage: item.useRemovedImage !== undefined ? item.useRemovedImage : true,
            usageCount: item.usageCount || 0,
            lastUsed: item.lastUsed,
            createdAt: item.created_at,
            updatedAt: item.updated_at
          };
        }
        throw new Error(response?.message || 'Creation failed');
      }
    }),

    // Set active person image
    setActivePersonImage: builder.mutation({
      query: (id) => ({
        url: API_ENDPOINTS.PERSON_IMAGE.ACTIVATE(id),
        method: 'PUT'
      }),
      invalidatesTags: (result, error, id) => [
        { type: 'PersonImage', id },
        { type: 'PersonImage', id: 'LATEST' },
        'PersonImage'
      ],
      transformResponse: (response) => {
        if (response?.personImage) {
          const item = response.personImage;
          return {
            id: item.id,
            fileName: item.fileName,
            image: item.image ? {
              id: item.image.id,
              url: item.image.url
            } : null,
            removedBgImage: item.removedBgImage ? {
              id: item.removedBgImage.id,
              url: item.removedBgImage.url
            } : null,
            thumbnail: item.thumbnail ? {
              id: item.thumbnail.id,
              url: item.thumbnail.url
            } : null,
            isActive: item.isActive,
            imageHash: item.imageHash,
            backgroundRemoved: item.backgroundRemoved || false,
            useRemovedImage: item.useRemovedImage !== undefined ? item.useRemovedImage : true,
            usageCount: item.usageCount || 0,
            lastUsed: item.lastUsed,
            createdAt: item.created_at,
            updatedAt: item.updated_at
          };
        }
        return response;
      }
    }),

    // Deactivate person image
    deactivatePersonImage: builder.mutation({
      query: (id) => ({
        url: API_ENDPOINTS.PERSON_IMAGE.DEACTIVATE(id),
        method: 'PUT'
      }),
      invalidatesTags: (result, error, id) => [
        { type: 'PersonImage', id },
        { type: 'PersonImage', id: 'LATEST' },
        'PersonImage'
      ]
    }),

    // Update person image
    updatePersonImage: builder.mutation({
      query: ({ id, ...updates }) => ({
        url: API_ENDPOINTS.PERSON_IMAGE.UPDATE(id),
        method: 'PUT',
        body: updates
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'PersonImage', id },
        'PersonImage'
      ]
    }),

    // Delete person image
    deletePersonImage: builder.mutation({
      query: (id) => ({
        url: API_ENDPOINTS.PERSON_IMAGE.DELETE(id),
        method: 'DELETE'
      }),
      invalidatesTags: (result, error, id) => [
        { type: 'PersonImage', id },
        { type: 'PersonImage', id: 'LATEST' },
        'PersonImage'
      ]
    }),

    // Validate image for virtual try-on
    validatePersonImage: builder.mutation({
      query: (imageData) => ({
        url: API_ENDPOINTS.PERSON_IMAGE.VALIDATE,
        method: 'POST',
        body: { imageData }
      }),
      transformResponse: (response) => {
        if (response?.validation) {
          return {
            isCompatible: response.validation.isCompatible,
            issues: response.validation.issues || [],
            recommendations: response.validation.recommendations || [],
            message: response.message
          };
        }
        return response;
      }
    }),

    // Get image upload recommendations
    getImageRecommendations: builder.query({
      query: () => ({
        url: API_ENDPOINTS.PERSON_IMAGE.RECOMMENDATIONS,
        method: 'GET'
      }),
      transformResponse: (response) => {
        return response?.recommendations || [];
      }
    }),

    // Remove background from uploaded person image
    removePersonBgFromUpload: builder.mutation({
      query: ({ file, personImageId }) => {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('personImageId', personImageId);
        return {
          url: API_ENDPOINTS.PERSON_IMAGE.REMOVE_BG_UPLOAD,
          method: 'POST',
          body: formData
        };
      },
      invalidatesTags: (result, error, { personImageId }) => [
        { type: 'PersonImage', id: personImageId },
        'PersonImage'
      ],
      transformResponse: (response) => {
        if (response?.success && response?.personImage) {
          const item = response.personImage;
          return {
            id: item.id,
            fileName: item.fileName,
            image: item.image ? {
              id: item.image.id,
              url: item.image.url
            } : null,
            removedBgImage: item.removedBgImage ? {
              id: item.removedBgImage.id,
              url: item.removedBgImage.url
            } : null,
            thumbnail: item.thumbnail ? {
              id: item.thumbnail.id,
              url: item.thumbnail.url
            } : null,
            isActive: item.isActive,
            imageHash: item.imageHash,
            backgroundRemoved: item.backgroundRemoved,
            useRemovedImage: item.useRemovedImage !== undefined ? item.useRemovedImage : true,
            usageCount: item.usageCount || 0,
            lastUsed: item.lastUsed,
            createdAt: item.created_at,
            updatedAt: item.updated_at
          };
        }
        throw new Error(response?.error || 'Background removal failed');
      }
    }),

    // Remove background from person image URL
    removePersonBgFromUrl: builder.mutation({
      query: ({ imageUrl, personImageId }) => ({
        url: API_ENDPOINTS.PERSON_IMAGE.REMOVE_BG_URL,
        method: 'POST',
        body: { imageUrl, personImageId }
      }),
      invalidatesTags: (result, error, { personImageId }) => [
        { type: 'PersonImage', id: personImageId },
        'PersonImage'
      ],
      transformResponse: (response) => {
        if (response?.success && response?.personImage) {
          const item = response.personImage;
          return {
            id: item.id,
            fileName: item.fileName,
            image: item.image ? {
              id: item.image.id,
              url: item.image.url
            } : null,
            removedBgImage: item.removedBgImage ? {
              id: item.removedBgImage.id,
              url: item.removedBgImage.url
            } : null,
            thumbnail: item.thumbnail ? {
              id: item.thumbnail.id,
              url: item.thumbnail.url
            } : null,
            isActive: item.isActive,
            imageHash: item.imageHash,
            backgroundRemoved: item.backgroundRemoved,
            useRemovedImage: item.useRemovedImage !== undefined ? item.useRemovedImage : true,
            usageCount: item.usageCount || 0,
            lastUsed: item.lastUsed,
            createdAt: item.created_at,
            updatedAt: item.updated_at
          };
        }
        throw new Error(response?.error || 'Background removal failed');
      }
    }),

    // Set person image as default
    setDefaultPersonImage: builder.mutation({
      query: (id) => ({
        url: `/person-images/${id}/set-default`,
        method: 'PUT'
      }),
      invalidatesTags: ['PersonImage'],
      transformResponse: (response) => {
        if (response?.personImage) {
          const item = response.personImage;
          return {
            id: item.id,
            fileName: item.fileName,
            displayName: item.displayName,
            isDefault: item.isDefault,
            image: item.image ? {
              id: item.image.id,
              url: item.image.url
            } : null,
            removedBgImage: item.removedBgImage ? {
              id: item.removedBgImage.id,
              url: item.removedBgImage.url
            } : null,
            thumbnail: item.thumbnail ? {
              id: item.thumbnail.id,
              url: item.thumbnail.url
            } : null,
            isActive: item.isActive,
            imageHash: item.imageHash,
            backgroundRemoved: item.backgroundRemoved,
            usageCount: item.usageCount || 0,
            lastUsed: item.lastUsed,
            createdAt: item.created_at,
            updatedAt: item.updated_at
          };
        }
        return response;
      }
    }),

    // Update person image display name
    updatePersonImageName: builder.mutation({
      query: ({ id, displayName }) => ({
        url: `/person-images/${id}`,
        method: 'PUT',
        body: { displayName }
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'PersonImage', id },
        'PersonImage'
      ]
    }),

    // Toggle background (use removed or original)
    togglePersonImageBackground: builder.mutation({
      query: (id) => ({
        url: `/person-images/${id}/toggle-background`,
        method: 'PUT'
      }),
      invalidatesTags: (result, error, id) => [
        { type: 'PersonImage', id },
        'PersonImage'
      ],
      transformResponse: (response) => {
        if (response?.personImage) {
          const item = response.personImage;
          return {
            id: item.id,
            fileName: item.fileName,
            displayName: item.displayName,
            isDefault: item.isDefault,
            image: item.image ? {
              id: item.image.id,
              url: item.image.url
            } : null,
            removedBgImage: item.removedBgImage ? {
              id: item.removedBgImage.id,
              url: item.removedBgImage.url
            } : null,
            thumbnail: item.thumbnail ? {
              id: item.thumbnail.id,
              url: item.thumbnail.url
            } : null,
            isActive: item.isActive,
            imageHash: item.imageHash,
            backgroundRemoved: item.backgroundRemoved,
            useRemovedImage: item.useRemovedImage,
            usageCount: item.usageCount || 0,
            lastUsed: item.lastUsed,
            createdAt: item.created_at,
            updatedAt: item.updated_at
          };
        }
        return response;
      }
    }),

    // Get VTO count for person image
    getPersonImageVTOCount: builder.query({
      query: (id) => `/person-images/${id}/vto-count`
    }),

    // Cascade delete person image and all associated VTO results
    cascadeDeletePersonImage: builder.mutation({
      query: (id) => ({
        url: `/person-images/${id}/cascade`,
        method: 'DELETE'
      }),
      invalidatesTags: ['PersonImage', 'VTOResult']
    })
  })
});

// Export hooks for the person image API
export const {
  useGetUserPersonImagesQuery,
  useGetLatestPersonImageQuery,
  useUploadPersonImageMutation,
  useCreatePersonImageWithFilesMutation,
  useSetActivePersonImageMutation,
  useDeactivatePersonImageMutation,
  useUpdatePersonImageMutation,
  useDeletePersonImageMutation,
  useValidatePersonImageMutation,
  useGetImageRecommendationsQuery,
  useRemovePersonBgFromUploadMutation,
  useRemovePersonBgFromUrlMutation,
  useSetDefaultPersonImageMutation,
  useUpdatePersonImageNameMutation,
  useTogglePersonImageBackgroundMutation,
  useGetPersonImageVTOCountQuery,
  useCascadeDeletePersonImageMutation
} = enhancedPersonImageApi;

export default enhancedPersonImageApi;